import React from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DiamondIcon from '@mui/icons-material/Diamond';

function HowItWorks() {
  const steps = [
    { step: '02', title: 'Secure Verification', desc: 'Execute your purchase through our encrypted, private payment gateway.', icon: <VerifiedUserIcon /> },
    { step: '03', title: 'Claim & Store', desc: 'Receive your digital certificate and physical asset storage details.', icon: <DiamondIcon /> }
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#050505' }}>
      <Container maxWidth="lg">
        
        {/* Header with Image - Improved */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: 4,
            mb: 2,
            flexWrap: 'wrap',
          }}>
            <Typography variant="h2" sx={{ 
              fontFamily: "'Playfair Display', serif", 
              color: '#ffffff', 
              fontSize: { xs: '2.2rem', md: '3.2rem' },
              fontWeight: 600,
              letterSpacing: '1px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              textShadow: '0 0 40px rgba(212, 175, 55, 0.1)',
            }}>
              The Acquisition Process
            </Typography>
            <Box 
              component="img"
              src="/images/diamondgoldcoin.png"
              alt="Diamond Gold Coin"
              sx={{
                height: { xs: '70px', md: '100px' },
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 40px rgba(212, 175, 55, 0.4)) drop-shadow(0 0 80px rgba(212, 175, 55, 0.15))',
                animation: 'floatCoin 4s ease-in-out infinite',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  filter: 'drop-shadow(0 0 60px rgba(212, 175, 55, 0.6)) drop-shadow(0 0 100px rgba(212, 175, 55, 0.2))',
                },
              }}
            />
          </Box>
          <style>
            {`
              @keyframes floatCoin {
                0%, 100% { transform: translateY(0px) rotate(0deg); }
                50% { transform: translateY(-8px) rotate(2deg); }
              }
            `}
          </style>
          <Typography variant="subtitle1" sx={{ 
            color: '#a1a1a1', 
            textAlign: 'center', 
            textTransform: 'uppercase', 
            letterSpacing: '4px', 
            fontSize: '0.75rem',
            fontWeight: 300,
          }}>
            Seamless, Secure, and Discreet
          </Typography>
          
          {/* Decorative gold line */}
          <Box sx={{ 
            width: '80px', 
            height: '2px', 
            margin: '16px auto 0',
            background: 'linear-gradient(90deg, transparent, #d4af37, transparent)',
            borderRadius: '2px',
          }} />
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {steps.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} sx={{ height: '100%' }}>
              <Box sx={{ textAlign: 'center', position: 'relative', height: '100%' }}>
                
                {/* Step Number Background - Larger */}
                <Typography 
                  variant="h1" 
                  sx={{ 
                    fontFamily: "'Playfair Display', serif", 
                    fontSize: '6rem', 
                    color: 'transparent', 
                    WebkitTextStroke: '1.5px rgba(212, 175, 55, 0.15)', 
                    position: 'absolute', 
                    top: '-20px', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    zIndex: 0, 
                    userSelect: 'none',
                    pointerEvents: 'none',
                    fontWeight: 700,
                  }}
                >
                  {item.step}
                </Typography>

                <Paper 
                  elevation={0} 
                  sx={{ 
                    position: 'relative', 
                    zIndex: 1, 
                    background: 'linear-gradient(145deg, #141414 0%, #0a0a0a 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.12)', 
                    borderRadius: '8px', 
                    padding: '35px 25px 30px',
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)', 
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.3), transparent)',
                      opacity: 0,
                      transition: 'opacity 0.5s ease',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '50%',
                      background: 'linear-gradient(to bottom, rgba(255,255,255,0.04), transparent)',
                      pointerEvents: 'none',
                    },
                    '&:hover': { 
                      borderColor: 'rgba(212, 175, 55, 0.4)',
                      transform: 'translateY(-8px) scale(1.01)', 
                      boxShadow: '0 30px 60px rgba(0, 0, 0, 0.9), 0 0 40px rgba(212, 175, 55, 0.1)',
                      '&::before': {
                        opacity: 1,
                      },
                      '& .process-icon': {
                        borderColor: '#d4af37',
                        color: '#d4af37',
                        backgroundColor: 'rgba(212, 175, 55, 0.15)',
                        boxShadow: '0 0 30px rgba(212, 175, 55, 0.15)',
                      },
                      '& .step-number': {
                        color: 'rgba(212, 175, 55, 0.3)',
                      }
                    } 
                  }}
                >
                  {/* Premium Icon Box - Larger */}
                  <Box 
                    className="process-icon"
                    sx={{ 
                      height: '55px', 
                      width: '55px',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: 2.5, 
                      borderRadius: '8px',
                      border: '1px solid rgba(192, 192, 192, 0.2)',
                      backgroundColor: 'rgba(192, 192, 192, 0.05)',
                      color: '#c0c0c0',
                      transition: 'all 0.4s ease',
                      '& svg': { fontSize: '1.8rem' } 
                    }}
                  >
                    {item.icon}
                  </Box>

                  {/* Title */}
                  <Typography variant="h6" sx={{ 
                    fontFamily: "'Playfair Display', serif", 
                    color: '#ffffff', 
                    mb: 1.5, 
                    height: '28px', 
                    fontSize: '1.2rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                  }}>
                    {item.title}
                  </Typography>

                  {/* Description */}
                  <Typography variant="body2" sx={{ 
                    color: '#a1a1a1', 
                    lineHeight: 1.7, 
                    fontSize: '0.85rem',
                    maxWidth: '280px',
                  }}>
                    {item.desc}
                  </Typography>

                  {/* Bottom decorative line on hover */}
                  <Box sx={{
                    width: '30px',
                    height: '2px',
                    background: 'linear-gradient(90deg, #d4af37, transparent)',
                    borderRadius: '2px',
                    mt: 2,
                    opacity: 0,
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      opacity: 1,
                      width: '50px',
                    }
                  }} />
                </Paper>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom decorative element */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mt: 6,
          gap: 2,
        }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: i % 2 === 0 ? '#d4af37' : 'rgba(212, 175, 55, 0.2)',
              opacity: i % 2 === 0 ? 1 : 0.3,
            }} />
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default HowItWorks;